/* eslint-disable react-hooks/exhaustive-deps */
import { Box, debounce, Stack } from "@mui/material";
import { InputBase } from "components/common/Input";
import useSearchParamsOperations from "hooks/useSearchParamsOperations";
import { useCallback, useEffect, useState } from "react";
import useInfiniteScroll from "react-infinite-scroll-hook";
import { useSearchParams } from "react-router-dom";
import { Location } from "types/location";
import { Pickup, PickupStatuses } from "types/pickup";
import PickupsFiltersMenu from "./PickupsFiltersMenu";

function parseFilterValues(searchParams: URLSearchParams, anyOfPrefix: string, singlePrefix?: string) {
  const anyOf = searchParams
    .getAll("filter[]")
    .find((filter) => filter.startsWith(anyOfPrefix))
    ?.slice(anyOfPrefix.length);
  if (anyOf) return anyOf.split(",").filter(Boolean);

  const single = singlePrefix
    ? searchParams
        .getAll("filter[]")
        .find((filter) => filter.startsWith(singlePrefix))
        ?.slice(singlePrefix.length)
    : undefined;
  return single ? [single] : [];
}

function sameValues(first: string[], second: string[]) {
  return first.length === second.length && first.every((value, index) => value === second[index]);
}

type PickupsFiltersToolbarProps = {
  pickups: Pickup[];
  locations: Location[];
  isLoading: boolean;
  hasNextPage: boolean;
  errorMessage: string | null;
  incrementCurrentPage: () => void;
};

export default function PickupsFiltersToolbar({
  pickups,
  locations,
  isLoading,
  hasNextPage,
  errorMessage,
  incrementCurrentPage,
}: PickupsFiltersToolbarProps) {
  const [searchParams] = useSearchParams();
  const { replace, deleteParam, deleteFieldInArrayUsingPrefix, replaceInArrayFieldUsingPrefix } =
    useSearchParamsOperations();

  const [pickupIds, setPickupIds] = useState(() => parseFilterValues(searchParams, "id,is_any_of,"));
  const [locationIds, setLocationIds] = useState(() =>
    parseFilterValues(searchParams, "location_id,is_any_of,", "location_id,is,")
  );
  const [statuses, setStatuses] = useState(() => parseFilterValues(searchParams, "state,is_any_of,"));
  const [pickupListItemRef] = useInfiniteScroll({
    loading: isLoading,
    hasNextPage,
    onLoadMore: incrementCurrentPage,
    disabled: !!errorMessage,
    rootMargin: "0px 0px 400px 0px",
  });

  useEffect(() => {
    const nextPickupIds = parseFilterValues(searchParams, "id,is_any_of,");
    const nextLocationIds = parseFilterValues(searchParams, "location_id,is_any_of,", "location_id,is,");
    const nextStatuses = parseFilterValues(searchParams, "state,is_any_of,");

    setPickupIds((current) => (sameValues(current, nextPickupIds) ? current : nextPickupIds));
    setLocationIds((current) => (sameValues(current, nextLocationIds) ? current : nextLocationIds));
    setStatuses((current) => (sameValues(current, nextStatuses) ? current : nextStatuses));
  }, [searchParams]);

  const handleSearchChange = useCallback(
    debounce((replaceParams: (field: string, value: string) => void, search: string) => {
      replaceParams("q", search);
    }, 200),
    []
  );

  const cleanFilters = useCallback(() => {
    setPickupIds([]);
    setLocationIds([]);
    setStatuses([]);
    deleteParam("filter[]");
    deleteParam("filter_link_operator");
  }, [deleteParam]);

  useEffect(() => {
    if (pickupIds.length > 0) {
      replaceInArrayFieldUsingPrefix("filter[]", "id,is_any_of", `id,is_any_of,${pickupIds.join(",")}`);
    } else {
      deleteFieldInArrayUsingPrefix("filter[]", "id,is_any_of");
    }
  }, [pickupIds]);

  useEffect(() => {
    deleteFieldInArrayUsingPrefix("filter[]", "location_id,is,");
    if (locationIds.length > 0) {
      replaceInArrayFieldUsingPrefix(
        "filter[]",
        "location_id,is_any_of",
        `location_id,is_any_of,${locationIds.join(",")}`
      );
    } else {
      deleteFieldInArrayUsingPrefix("filter[]", "location_id,is_any_of");
    }
  }, [locationIds]);

  useEffect(() => {
    if (statuses.length > 0) {
      replaceInArrayFieldUsingPrefix("filter[]", "state,is_any_of", `state,is_any_of,${statuses.join(",")}`);
    } else {
      deleteFieldInArrayUsingPrefix("filter[]", "state,is_any_of");
    }
  }, [statuses]);

  return (
    <Box sx={{ maxWidth: { sm: 720 }, my: 2 }}>
      <Stack direction="row" alignItems="center" gap={0.5} sx={{ minWidth: 0, width: "100%" }}>
        <InputBase
          placeholder="Search pickups..."
          defaultValue={searchParams.get("q") || ""}
          sx={{
            flex: 1,
            minWidth: 0,
            maxWidth: 500,
            marginRight: 1,
            "&:hover, &:focus": { zIndex: 2 },
          }}
          onChange={(event) => handleSearchChange(replace, event.target.value)}
        />
        <PickupsFiltersMenu
          pickupIds={pickupIds}
          onPickupIdsChange={setPickupIds}
          pickups={pickups}
          locationIds={locationIds}
          onLocationIdsChange={setLocationIds}
          locations={locations}
          statuses={statuses}
          onStatusesChange={setStatuses}
          statusOptions={PickupStatuses}
          onClearAll={cleanFilters}
          pickupListItemRef={pickupListItemRef}
        />
      </Stack>
    </Box>
  );
}
